import SEOHead from '../../components/SEOHead';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import BrandsSection from './components/BrandsSection';
import ServicesSection from './components/ServicesSection';
import ContactSection from './components/ContactSection';
import NewsletterSection from './components/NewsletterSection';

export default function HomePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: 'Villarreal Enterprise Group',
        description: 'Las Vegas DMV services — vehicle registration, title transfers, VIN inspections, and document preparation.',
        publisher: { '@id': `${siteUrl}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteUrl}/services?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'Villarreal Enterprise Group',
        legalName: 'The Villarreal Organization, Inc',
        alternateName: ['Speedy DMV Services', 'DMV-Xpress Services'],
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/1aff587802347e8a3b9191c90eecded3/c67bf49259dce1e180324b942fef6f74.png',
          width: 400,
          height: 400,
        },
        description: 'Nevada-based business group operating Speedy DMV Services™ and DMV-Xpress Services™, providing comprehensive DMV and document services in Las Vegas since 2008.',
        foundingDate: '2008',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Las Vegas',
          addressRegion: 'NV',
          postalCode: '89101',
          addressCountry: 'US',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 36.1699,
          longitude: -115.1398,
        },
        areaServed: [
          { '@type': 'City', name: 'Las Vegas', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Henderson', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'North Las Vegas', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Paradise', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Sunrise Manor', containedInPlace: { '@type': 'State', name: 'Nevada' } },
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          areaServed: 'US',
          availableLanguage: ['English', 'Spanish'],
          email: 'info@villarrealgroup.com',
        },
        sameAs: [],
      },
      {
        '@type': 'LocalBusiness',
        '@id': `${siteUrl}/#localbusiness`,
        name: 'Villarreal Enterprise Group',
        image: 'https://static.readdy.ai/image/1aff587802347e8a3b9191c90eecded3/c67bf49259dce1e180324b942fef6f74.png',
        url: siteUrl,
        priceRange: '$$',
        currenciesAccepted: 'USD',
        paymentAccepted: 'Cash, Credit Card, Debit Card',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Las Vegas',
          addressRegion: 'NV',
          postalCode: '89101',
          addressCountry: 'US',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 36.1699,
          longitude: -115.1398,
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '17:00',
          },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          reviewCount: '10000',
          bestRating: '5',
          worstRating: '1',
        },
        hasMap: 'https://maps.google.com/?q=Las+Vegas,+NV',
      },
      {
        '@type': 'Service',
        '@id': `${siteUrl}/#service`,
        serviceType: 'DMV Services',
        provider: { '@id': `${siteUrl}/#organization` },
        areaServed: { '@type': 'State', name: 'Nevada' },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'DMV Services',
          itemListElement: [
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Vehicle Registration & Renewal',
                description: 'Quick and efficient vehicle registration services for new and existing vehicles in Las Vegas, Nevada.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Title Transfers & Tag Services',
                description: 'Complete title transfer services and license plate tag processing for Clark County residents.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'VIN Inspections',
                description: 'Professional VIN verification and inspection services for all vehicle types in Nevada.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Document Preparation',
                description: 'Professional document preparation and review services for all DMV needs in Las Vegas.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Smog Check Guidance',
                description: 'Nevada smog check requirement assessment, referrals, and failed test resolution guidance.',
              },
            },
            {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Concierge Client Support',
                description: 'Personalized one-on-one DMV consultation and multilingual assistance in Las Vegas.',
              },
            },
          ],
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What DMV services does Villarreal Enterprise Group offer in Las Vegas?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Villarreal Enterprise Group offers vehicle registration and renewal, title transfers, VIN inspections, smog check guidance, document preparation, and concierge client support through its two brands: Speedy DMV Services™ and DMV-Xpress Services™.',
            },
          },
          {
            '@type': 'Question',
            name: 'What areas does Villarreal Enterprise Group serve?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We serve the greater Las Vegas Valley including Las Vegas, Henderson, North Las Vegas, Paradise, and Sunrise Manor — all within Clark County, Nevada.',
            },
          },
          {
            '@type': 'Question',
            name: 'What is the difference between Speedy DMV Services and DMV-Xpress Services?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Speedy DMV Services™ focuses on fast, everyday DMV transactions like vehicle registration and title transfers. DMV-Xpress Services™ specializes in complex transactions including VIN inspections, salvage title processing, and corporate fleet accounts.',
            },
          },
          {
            '@type': 'Question',
            name: 'How long has Villarreal Enterprise Group been in business?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Villarreal Enterprise Group has been serving Las Vegas since 2008 — over 15 years of experience helping Clark County residents with their DMV needs.',
            },
          },
        ],
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="Las Vegas DMV Services | Villarreal Enterprise Group - Speedy DMV & DMV-Xpress"
        description="Villarreal Enterprise Group operates Speedy DMV Services™ and DMV-Xpress Services™ in Las Vegas. Fast vehicle registration, title transfers, VIN inspections, and document services. Serving Clark County with 15+ years experience and 10,000+ satisfied clients."
        keywords="Las Vegas DMV services, Nevada vehicle registration, DMV title transfer, VIN inspection Las Vegas, Speedy DMV Services, DMV-Xpress Services"
        canonical={siteUrl}
        ogImage="https://static.readdy.ai/image/1aff587802347e8a3b9191c90eecded3/c67bf49259dce1e180324b942fef6f74.png"
        schema={schema}
        schemaId="schema-home"
      />
      <HeroSection />
      <AboutSection />
      <BrandsSection />
      <ServicesSection />
      <ContactSection />
      <NewsletterSection />
    </>
  );
}
